import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  Box,
  Typography
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import color from 'utils/colors';
import { validateEmail, formatRutDisplay } from 'utils/functions';

const useStyles = makeStyles(() => ({
  dialogTitle: {
    color: color.purpleSecondary,
    fontWeight: 'bold',
    fontSize: '20px',
    padding: '24px 24px 8px 24px'
  },
  dialogContent: {
    padding: '6px 24px 24px 24px',
    marginTop: '2px',
    '& .MuiGrid-container': {
      marginTop: '1px'
    }
  },
  textField: {
    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        borderColor: '#E0E0E0',
      },
      '&:hover fieldset': {
        borderColor: color.purpleSecondary,
      },
      '&.Mui-focused fieldset': {
        borderColor: color.purpleSecondary,
      },
    },
    '& .MuiInputLabel-root': {
      color: color.darkGrey,
      '&.Mui-focused': {
        color: color.purpleSecondary,
      },
    },
  },
  cancelButton: {
    color: color.purpleSecondary,
    borderColor: color.purpleSecondary,
    textTransform: 'none',
    '&:hover': {
      borderColor: color.purpleSecondary,
      backgroundColor: `${color.purpleSecondary}10`,
    }
  },
  saveButton: {
    backgroundColor: color.purpleSecondary,
    color: color.white,
    textTransform: 'none',
    '&:hover': {
      backgroundColor: color.purpleSecondary,
    },
    '&.Mui-disabled': {
      backgroundColor: '#E0E0E0',
      color: '#A0A0A0',
    }
  }
}));


function EditUserModal({ open, onClose, user, onSave }) {
  const classes = useStyles();
  
  // Estados para los campos del formulario
  const [formData, setFormData] = useState({
    // Para personas jurídicas
    razonSocial: '',
    rut: '',
    correo: '',
    telefono: '',
    direccion: '',
    
    // Para personas naturales
    nombre: '',
    apellido: '',
  });

  // Estado original para comparar cambios
  const [originalData, setOriginalData] = useState({});

  // Estados para validaciones
  const [validationErrors, setValidationErrors] = useState({
    correo: '',
    telefono: '',
    nombre: '',
    apellido: '',
    razonSocial: ''
  });

  // Inicializar formulario cuando se abre el modal
  useEffect(() => {
    if (open && user) {
      const initialData = {
        // Para personas jurídicas
        razonSocial: user.names || '',
        rut: user.rut || '',
        correo: user.email || '',
        telefono: user.phone || '',
        direccion: user.address || '',
        
        // Para personas naturales
        nombre: user.names || '',
        apellido: user.surnames || '',
      };
      
      setFormData(initialData);
      setOriginalData(initialData);
      
      // Limpiar errores de validación al abrir el modal
      setValidationErrors({
        correo: '',
        telefono: '',
        nombre: '',
        apellido: '',
        razonSocial: ''
      });
    }
  }, [open, user]);

  // Funciones de validación
  const validateEmailField = (email) => {
    if (!email.trim()) {
      return 'El correo es requerido';
    }
    if (!validateEmail(email)) {
      return 'Ingrese un correo válido';
    }
    return '';
  };

  const validatePhoneField = (phone) => {
    if (!phone.trim()) {
      return 'El teléfono es requerido';
    }
    
    // Remover espacios y caracteres especiales excepto + y números
    const cleanPhone = phone.replace(/[^+0-9]/g, '');
    
    // Validar formato: 9 dígitos, 56+9 dígitos, o +56+9 dígitos
    if (/^[0-9]{9}$/.test(cleanPhone)) {
      return ''; // Válido: 9 dígitos (ej: 987654321)
    } else if (/^56[0-9]{9}$/.test(cleanPhone)) {
      return ''; // Válido: código país sin + (ej: 56987654321)
    } else if (/^\+56[0-9]{9}$/.test(cleanPhone)) {
      return ''; // Válido: código país con + (ej: +56987654321)
    } else {
      return 'Ingrese un teléfono válido (ej: 987654321, 56987654321 o +56987654321)';
    }
  };

  const validateNameField = (name) => {
    if (!name.trim()) {
      return 'El nombre es requerido';
    }
    if (name.trim().length < 2) {
      return 'El nombre debe tener al menos 2 caracteres';
    }
    return '';
  };

  const validateSurnameField = (surname) => {
    if (!surname.trim()) {
      return 'El apellido es requerido';
    }
    if (surname.trim().length < 2) {
      return 'El apellido debe tener al menos 2 caracteres';
    }
    return '';
  };

  const validateBusinessNameField = (businessName) => {
    if (!businessName.trim()) {
      return 'La razón social es requerida';
    }
    if (businessName.trim().length < 2) {
      return 'La razón social debe tener al menos 2 caracteres';
    }
    return '';
  };

  // Verificar si hay cambios y si el formulario es válido
  const hasChanges = useMemo(() => {
    return JSON.stringify(formData) !== JSON.stringify(originalData);
  }, [formData, originalData]);

  const isFormValid = useMemo(() => {
    const emailError = validateEmailField(formData.correo);
    const phoneError = validatePhoneField(formData.telefono);

    // Validaciones específicas según el tipo de usuario
    if (user?.userType === 'juridica') {
      const businessNameError = validateBusinessNameField(formData.razonSocial);
      return !emailError && !phoneError && !businessNameError;
    } else {
      const nameError = validateNameField(formData.nombre);
      const surnameError = validateSurnameField(formData.apellido);
      return !emailError && !phoneError && !nameError && !surnameError;
    }
  }, [formData.correo, formData.telefono, formData.nombre, formData.apellido, formData.razonSocial, user?.userType]);

  const handleInputChange = (field) => (event) => {
    const value = event.target.value;
    
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Validar en tiempo real
    if (field === 'correo') {
      setValidationErrors(prev => ({
        ...prev,
        correo: validateEmailField(value)
      }));
    } else if (field === 'telefono') {
      setValidationErrors(prev => ({
        ...prev,
        telefono: validatePhoneField(value)
      }));
    } else if (field === 'nombre') {
      setValidationErrors(prev => ({
        ...prev,
        nombre: validateNameField(value)
      }));
    } else if (field === 'apellido') {
      setValidationErrors(prev => ({
        ...prev,
        apellido: validateSurnameField(value)
      }));
    } else if (field === 'razonSocial') {
      setValidationErrors(prev => ({
        ...prev,
        razonSocial: validateBusinessNameField(value)
      }));
    }
  };

  const handleCancel = () => {
    setFormData(originalData);
    onClose();
  };

  const handleSave = () => {
    if (hasChanges && isFormValid) {
      // Preparar datos para enviar según el tipo de usuario
      const dataToSave = {
        id: user.id,
        email: formData.correo,
        phone: formData.telefono,
      };

      if (user.userType === 'juridica') {
        dataToSave.names = formData.razonSocial;
        dataToSave.address = formData.direccion;
      } else {
        dataToSave.names = formData.nombre;
        dataToSave.surnames = formData.apellido;
      }

      // RUT is intentionally excluded - not editable

      onSave(dataToSave);
    }
  };

  if (!user) return null;

  const isJuridica = user.userType === 'juridica';
  const modalTitle = isJuridica ? 'Editar Persona Jurídica' : 'Editar Persona Natural';

  return (
    <Dialog 
      open={open} 
      onClose={handleCancel}
      maxWidth="sm"
      fullWidth
      scroll="body"
      PaperProps={{
        sx: {
          borderRadius: '8px',
          maxHeight: '90vh',
          overflow: 'visible'
        }
      }}
    >
      <DialogTitle className={classes.dialogTitle}>
        {modalTitle}
      </DialogTitle>
      
      <DialogContent className={classes.dialogContent}>
        <Box sx={{ height: '2px' }} />
        <Grid container spacing={3}>
          {isJuridica ? (
            // Layout para Persona Jurídica
            <>
              {/* Razón Social - Ancho completo */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Razón Social"
                  value={formData.razonSocial}
                  onChange={handleInputChange('razonSocial')}
                  className={classes.textField}
                  InputLabelProps={{ shrink: true }}
                  error={!!validationErrors.razonSocial}
                  helperText={validationErrors.razonSocial}
                />
              </Grid>
              
              {/* RUT y Correo - En la misma fila */}
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="RUT"
                  value={formatRutDisplay(formData.rut)}
                  onChange={handleInputChange('rut')}
                  className={classes.textField}
                  InputLabelProps={{ shrink: true }}
                  disabled
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Correo"
                  type="email"
                  value={formData.correo}
                  onChange={handleInputChange('correo')}
                  className={classes.textField}
                  InputLabelProps={{ shrink: true }}
                  error={!!validationErrors.correo}
                  helperText={validationErrors.correo}
                />
              </Grid>
              
              {/* Teléfono y Dirección - En la misma fila */}
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Teléfono"
                  value={formData.telefono}
                  onChange={handleInputChange('telefono')}
                  className={classes.textField}
                  InputLabelProps={{ shrink: true }}
                  error={!!validationErrors.telefono}
                  helperText={validationErrors.telefono}
                  placeholder="Ej: 987654321 o +56987654321"
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Dirección"
                  value={formData.direccion}
                  onChange={handleInputChange('direccion')}
                  className={classes.textField}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </>
          ) : (
            // Layout para Persona Natural
            <>
              {/* Nombre y Apellido - En la misma fila */}
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Nombre"
                  value={formData.nombre}
                  onChange={handleInputChange('nombre')}
                  className={classes.textField}
                  InputLabelProps={{ shrink: true }}
                  error={!!validationErrors.nombre}
                  helperText={validationErrors.nombre}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Apellido"
                  value={formData.apellido}
                  onChange={handleInputChange('apellido')}
                  className={classes.textField}
                  InputLabelProps={{ shrink: true }}
                  error={!!validationErrors.apellido}
                  helperText={validationErrors.apellido}
                />
              </Grid>
              
              {/* RUT y Correo - En la misma fila */}
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="RUT"
                  value={formatRutDisplay(formData.rut)}
                  onChange={handleInputChange('rut')}
                  className={classes.textField}
                  InputLabelProps={{ shrink: true }}
                  disabled
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Correo"
                  type="email"
                  value={formData.correo}
                  onChange={handleInputChange('correo')}
                  className={classes.textField}
                  InputLabelProps={{ shrink: true }}
                  error={!!validationErrors.correo}
                  helperText={validationErrors.correo}
                />
              </Grid>
              
              {/* Teléfono - Ancho completo */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Teléfono"
                  value={formData.telefono}
                  onChange={handleInputChange('telefono')}
                  className={classes.textField}
                  InputLabelProps={{ shrink: true }}
                  error={!!validationErrors.telefono}
                  helperText={validationErrors.telefono}
                  placeholder="Ej: 987654321 o +56987654321"
                />
              </Grid>
            </>
          )}
        </Grid>
      </DialogContent>
      
      <DialogActions sx={{ padding: '16px 24px 24px 24px', justifyContent: 'space-between' }}>
        <Button
          onClick={handleCancel}
          variant="outlined"
          className={classes.cancelButton}
        >
          Cancelar
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          className={classes.saveButton}
          disabled={!hasChanges || !isFormValid}
        >
          Guardar
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default EditUserModal;